import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  handleReset = (): void => {
    this.setState({ error: null });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) {
      return this.props.children;
    }

    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 px-6 text-center">
        <div className="w-14 h-14 rounded-full bg-subtle flex items-center justify-center text-danger">
          <AlertTriangle className="w-7 h-7" />
        </div>
        <div className="space-y-1">
          <h1 className="text-lg font-semibold text-ink-primary">
            Algo deu errado ao renderizar esta página
          </h1>
          <p className="text-sm text-ink-tertiary max-w-md">
            {error.message || 'Erro desconhecido. Tente recarregar a página.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={this.handleReset}
            className="px-4 py-2 text-sm font-medium border border-border text-ink-secondary bg-surface rounded-lg hover:bg-subtle transition-colors"
          >
            Tentar novamente
          </button>
          <button
            onClick={this.handleReload}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-ink-primary text-canvas rounded-lg hover:bg-ink-secondary transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Recarregar
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
